import { Link } from 'react-router-dom';
import { useFinance } from '@/hooks/useFinance';
import { formatDate, formatMoney } from '@/lib/finance';
import { TX_TYPE_META } from '@/lib/credit';
import { FinanceSection, FinanceEmpty, FinanceStatCard } from '../components/FinanceUI';

export default function FinanceCreditPage() {
  const finance = useFinance();
  const { currency, credit } = finance;
  const { customers, transactions, balances, stats, loading } = credit;

  const withCredit = customers
    .map((c) => ({ customer: c, balance: balances[c.id] || 0 }))
    .filter((r) => r.balance > 0)
    .sort((a, b) => b.balance - a.balance);

  // Top débiteurs : les 5 plus gros montants dus, plafond signalé si dépassé.
  const topDebtors = withCredit.slice(0, 5);
  const maxBalance = Math.max(1, ...topDebtors.map((r) => r.balance));

  const recent = transactions.slice(0, 8);

  const error = finance.error || credit.error;

  if (loading) {
    return <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
        <p className="text-sm text-foreground-600 mb-3">{error}</p>
        <button onClick={finance.reload} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <FinanceStatCard label="Total à recevoir" value={formatMoney(stats.totalOutstanding, currency)} icon="ri-user-received-line" tone="primary" />
        <FinanceStatCard label="Clients débiteurs" value={String(stats.customersWithCredit)} icon="ri-group-line" tone="secondary" />
        <FinanceStatCard label="Crédits ce mois" value={formatMoney(stats.creditsThisMonth, currency)} icon="ri-arrow-down-line" tone="accent" />
        <FinanceStatCard label="Encaissé ce mois" value={formatMoney(stats.paymentsThisMonth, currency)} icon="ri-arrow-up-line" tone="primary" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Link to="/dashboard/credit" className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer no-underline">
          <i className="ri-book-2-line"></i>Ouvrir le carnet de crédit
        </Link>
        <span className="text-xs text-foreground-400">Zifek Finance réutilise directement vos données du carnet de crédit — aucune duplication.</span>
      </div>

      {stats.needingAttention > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg border bg-red-50 text-red-600 border-red-100">
          <i className="ri-shield-flash-line text-base flex-shrink-0"></i>
          <span className="flex-1 min-w-0 text-sm">
            {stats.needingAttention} client{stats.needingAttention > 1 ? 's' : ''} {stats.needingAttention > 1 ? 'ont' : 'a'} atteint son plafond de crédit.
          </span>
        </div>
      )}

      <FinanceSection title="Top débiteurs" icon="ri-trophy-line" action={<span className="text-xs text-foreground-400 whitespace-nowrap">Triés par montant dû</span>}>
        {topDebtors.length === 0 ? (
          <FinanceEmpty icon="ri-user-received-line" title="Aucun débiteur" hint="Personne ne vous doit d'argent pour le moment." />
        ) : (
          <div className="space-y-3">
            {topDebtors.map(({ customer, balance }, index) => {
              const overLimit = customer.credit_limit != null && customer.credit_limit > 0 && balance >= customer.credit_limit;
              const pct = Math.max(4, (balance / maxBalance) * 100);
              return (
                <Link key={customer.id} to={`/dashboard/credit/customer/${customer.id}`} className="block no-underline">
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${index === 0 ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'}`}>
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground-900 truncate">{customer.full_name}</span>
                        {overLimit && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-medium whitespace-nowrap flex items-center gap-0.5">
                            <i className="ri-shield-flash-line"></i>Plafond dépassé
                          </span>
                        )}
                      </div>
                      <div className="mt-1 h-2 bg-background-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${overLimit ? 'bg-red-400' : 'bg-primary-500'}`} style={{ width: `${pct}%` }}></div>
                      </div>
                      <div className="text-[11px] text-foreground-400 mt-1">
                        {customer.phone || 'Sans téléphone'}
                        {customer.credit_limit ? ` · Plafond ${formatMoney(customer.credit_limit, currency)}` : ''}
                      </div>
                    </div>
                    <span className="text-sm font-bold text-primary-700 whitespace-nowrap">{formatMoney(balance, currency)}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </FinanceSection>

      <FinanceSection title={`Soldes clients (${withCredit.length})`} icon="ri-user-follow-line">
        {withCredit.length === 0 ? (
          <FinanceEmpty icon="ri-user-received-line" title="Aucune créance en cours" hint="Vos clients n'ont aucun solde débiteur pour le moment." />
        ) : (
          <div className="divide-y divide-background-200/70">
            {withCredit.map(({ customer, balance }) => (
              <Link key={customer.id} to={`/dashboard/credit/customer/${customer.id}`} className="flex items-center gap-3 py-3 no-underline">
                <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {(customer.full_name || 'C').charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground-900 truncate flex items-center gap-2">
                    {customer.full_name}
                    {customer.credit_limit != null && customer.credit_limit > 0 && balance >= customer.credit_limit && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-medium whitespace-nowrap">Plafond dépassé</span>
                    )}
                  </div>
                  <div className="text-xs text-foreground-400">{customer.phone || 'Sans téléphone'}{customer.credit_limit ? ` · Plafond ${formatMoney(customer.credit_limit, currency)}` : ''}</div>
                </div>
                <span className="text-sm font-bold text-primary-700 whitespace-nowrap">{formatMoney(balance, currency)}</span>
              </Link>
            ))}
          </div>
        )}
      </FinanceSection>

      <FinanceSection title="Derniers mouvements de crédit" icon="ri-history-line">
        {recent.length === 0 ? (
          <p className="text-sm text-foreground-400">Aucun mouvement de crédit enregistré.</p>
        ) : (
          <div className="divide-y divide-background-200/70">
            {recent.map((tx) => {
              const meta = TX_TYPE_META[tx.type];
              const name = customers.find((c) => c.id === tx.customer_id)?.full_name || 'Client';
              return (
                <div key={tx.id} className="flex items-center gap-3 py-2.5">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${meta.className}`}>{meta.label}</span>
                  <span className="flex-1 min-w-0 text-sm text-foreground-800 truncate">{name}{tx.description ? ` — ${tx.description}` : ''}</span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{formatDate(tx.created_at)}</span>
                  <span className={`text-sm font-semibold whitespace-nowrap ${tx.type === 'payment' ? 'text-primary-700' : 'text-foreground-800'}`}>{formatMoney(Number(tx.amount), currency)}</span>
                </div>
              );
            })}
          </div>
        )}
      </FinanceSection>
    </div>
  );
}